/**
 * Admin "Pricing & Tiers" panel — surfaces:
 *   1. Stage-2 auto-trigger progress per audience (reservation count vs threshold)
 *      and a manual override to unlock paid billing without waiting for the count
 *   2. The full pre-launch reservation waitlist (from POST /api/pricing/reservations)
 *   3. A manual tier setter so admins can flip a specific user into Pro / Pro+
 *      (used during the early enrollment window before Stripe billing is wired)
 *
 * Endpoints used:
 *   GET  /api/admin/pricing/stage-status
 *   POST /api/admin/pricing/stage-status/manual-unlock?audience=
 *   GET  /api/admin/pricing/reservations
 *   PUT  /api/admin/pricing/user-tier   { user_id, tier_id }
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, Sparkles, CheckCircle2, Clock, Unlock, UserCog, Crown, ArrowUpRight,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIER_OPTIONS = [
  { id: 'clinician.starter', label: 'Clinician · Starter (12%)' },
  { id: 'clinician.pro', label: 'Clinician · Pro ($49/mo · 8%)' },
  { id: 'clinician.pro_plus', label: 'Clinician · Pro+ ($149/mo · 5%)' },
  { id: 'family.basic', label: 'Family · Basic (free)' },
  { id: 'family.remote_caregiver', label: 'Family · Remote Caregiver ($19/mo)' },
];

export function AdminPricingPanel({ getAuthHeader }) {
  const [stageRows, setStageRows] = useState([]);
  const [threshold, setThreshold] = useState(25);
  const [reservations, setReservations] = useState([]);
  const [byPlan, setByPlan] = useState({});
  const [byDiscipline, setByDiscipline] = useState({});
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(null);
  const [tierUserId, setTierUserId] = useState('');
  const [tierId, setTierId] = useState('clinician.pro');
  const [updatingTier, setUpdatingTier] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [stageRes, resRes] = await Promise.all([
        axios.get(`${API}/admin/pricing/stage-status`, getAuthHeader()),
        axios.get(`${API}/admin/pricing/reservations`, getAuthHeader()),
      ]);
      setStageRows(stageRes.data?.rows || []);
      setThreshold(stageRes.data?.threshold || 25);
      setReservations(resRes.data?.reservations || []);
      setByPlan(resRes.data?.by_plan || {});
      setByDiscipline(resRes.data?.by_discipline || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load pricing admin data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const manualUnlock = async (audience) => {
    setUnlocking(audience);
    try {
      await axios.post(`${API}/admin/pricing/stage-status/manual-unlock?audience=${audience}`, {}, getAuthHeader());
      toast.success(`Stage 2 unlocked for ${audience}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not unlock');
    } finally {
      setUnlocking(null);
    }
  };

  const setUserTier = async (e) => {
    e?.preventDefault?.();
    if (!tierUserId.trim() || !tierId) return;
    setUpdatingTier(true);
    try {
      await axios.put(`${API}/admin/pricing/user-tier`, { user_id: tierUserId.trim(), tier_id: tierId }, getAuthHeader());
      toast.success(`Tier ${tierId} set for ${tierUserId.trim()}`);
      setTierUserId('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not set tier');
    } finally {
      setUpdatingTier(false);
    }
  };

  if (loading) {
    return (
      <div className="py-12 flex items-center justify-center text-cn-text-muted" data-testid="admin-pricing-loading">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading pricing &amp; reservations…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-pricing-panel">
      {/* Stage 2 progress */}
      <div className="grid sm:grid-cols-3 gap-4" data-testid="stage-2-progress">
        {stageRows.map((row) => {
          const pct = Math.min(100, row.progress_pct || 0);
          return (
            <div key={row.audience} className="cn-card p-5" data-testid={`stage-row-${row.audience}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">{row.audience}</p>
                  <p className="font-display font-bold text-2xl text-cn-text dark:text-white mt-1" data-testid={`stage-count-${row.audience}`}>
                    {row.reservation_count} <span className="text-sm text-cn-text-muted font-normal">/ {row.threshold}</span>
                  </p>
                </div>
                {row.triggered ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px] font-bold" data-testid={`stage-unlocked-${row.audience}`}>
                    <CheckCircle2 className="w-3 h-3" /> Unlocked
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 text-[11px] font-bold">
                    <Clock className="w-3 h-3" /> Waiting
                  </span>
                )}
              </div>
              <div className="mt-3 h-2 rounded-full bg-cn-border/40 overflow-hidden">
                <div className={`h-full rounded-full transition-all duration-500 ${row.triggered ? 'bg-emerald-500' : 'bg-cn-primary'}`} style={{ width: `${pct}%` }} />
              </div>
              <p className="mt-2 text-xs text-cn-text-muted">
                {row.triggered
                  ? `Triggered ${row.triggered_at ? new Date(row.triggered_at).toLocaleDateString() : ''}`
                  : `${(row.threshold - row.reservation_count).toLocaleString()} more reservations to unlock Stripe billing.`}
              </p>
              {!row.triggered && (
                <button
                  type="button"
                  onClick={() => manualUnlock(row.audience)}
                  disabled={unlocking === row.audience}
                  className="cn-btn cn-btn-ghost !py-2 mt-3 w-full justify-center text-xs disabled:opacity-50"
                  data-testid={`stage-unlock-${row.audience}`}
                >
                  {unlocking === row.audience ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unlock className="w-3.5 h-3.5" />}
                  Manual unlock
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Manual user-tier setter */}
      <form
        onSubmit={setUserTier}
        className="cn-card p-5 sm:p-6 space-y-3"
        data-testid="manual-user-tier-form"
      >
        <div className="flex items-center gap-2">
          <Crown className="w-4 h-4 text-cn-primary" />
          <h3 className="font-display font-bold text-cn-text dark:text-white">Manually set a user&apos;s tier</h3>
        </div>
        <p className="text-sm text-cn-text-muted">
          Used during the early enrollment window before subscription billing is wired — flip a user to Pro / Pro+ so they immediately benefit from the lower take rate and unlocked features. Find the user id in the <strong>Users</strong> tab.
        </p>
        <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">User id</label>
            <input
              required
              value={tierUserId}
              onChange={(e) => setTierUserId(e.target.value)}
              placeholder="user-uuid"
              className="cn-input mt-1.5"
              data-testid="manual-tier-user-id"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider font-bold text-cn-text-muted">New tier</label>
            <select
              value={tierId}
              onChange={(e) => setTierId(e.target.value)}
              className="cn-input mt-1.5 w-full"
              data-testid="manual-tier-select"
            >
              {TIER_OPTIONS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>
          <button
            type="submit"
            disabled={updatingTier || !tierUserId.trim()}
            className="cn-btn cn-btn-primary disabled:opacity-50 inline-flex items-center justify-center gap-1.5"
            data-testid="manual-tier-submit"
          >
            {updatingTier ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCog className="w-4 h-4" />}
            Apply
          </button>
        </div>
      </form>

      {/* Channel attribution — reservation-by-discipline rollup. Each
          discipline-specific landing page (/for-<slug>) stamps its slug
          on the reservation payload; this surfaces conversion-per-channel
          at a glance. */}
      {Object.keys(byDiscipline).length > 0 && (
        <div className="cn-card p-5" data-testid="discipline-attribution">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-cn-primary" />
            <h3 className="font-display font-bold text-cn-text dark:text-white">Reservations by acquisition channel</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(byDiscipline)
              .sort((a, b) => b[1] - a[1])
              .map(([discipline, count]) => (
                <span
                  key={discipline}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cn-bone-100 text-cn-text border border-cn-border text-xs font-semibold"
                  data-testid={`discipline-count-${discipline}`}
                >
                  <span className="font-mono text-cn-primary">{discipline}</span>
                  <span className="text-cn-text-muted">·</span>
                  <span className="font-bold">{count}</span>
                </span>
              ))}
          </div>
          <p className="mt-3 text-[11px] text-cn-text-muted">
            <span className="font-mono">direct</span> = reservation made outside a discipline-specific landing page. The rest are discipline slugs from <span className="font-mono">/for-&lt;slug&gt;</span> landing pages.
          </p>
        </div>
      )}

      {/* Reservation list */}
      <div className="cn-card overflow-hidden" data-testid="reservations-list">
        <div className="px-5 py-4 border-b border-cn-border flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-cn-primary" />
            <h3 className="font-display font-bold text-cn-text dark:text-white">Pre-launch reservations</h3>
            <span className="text-xs text-cn-text-muted">({reservations.length})</span>
          </div>
          <div className="flex items-center gap-2">
            {Object.entries(byPlan).map(([plan, count]) => (
              <span
                key={plan}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-cn-primary/10 text-cn-primary border border-cn-primary/20 text-[11px] font-bold"
                data-testid={`reservations-count-${plan}`}
              >
                {plan} · {count}
              </span>
            ))}
            <button
              type="button"
              onClick={load}
              className="cn-btn cn-btn-ghost !py-1.5 !px-3 text-xs"
              data-testid="reservations-refresh"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>
        </div>
        {reservations.length === 0 ? (
          <div className="px-5 py-10 text-center text-cn-text-muted" data-testid="reservations-empty">
            No reservations yet — they&apos;ll land here as users click <strong>Reserve my Pro spot</strong> across the app.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-cn-text-muted border-b border-cn-border">
                  <th className="px-5 py-3 font-bold">Date</th>
                  <th className="px-5 py-3 font-bold">Email</th>
                  <th className="px-5 py-3 font-bold">Name</th>
                  <th className="px-5 py-3 font-bold">Plan</th>
                  <th className="px-5 py-3 font-bold">Channel</th>
                  <th className="px-5 py-3 font-bold text-right">Monthly earnings</th>
                </tr>
              </thead>
              <tbody>
                {reservations.map((r, i) => (
                  <tr key={r.id || i} className="border-b border-cn-border/60 last:border-0" data-testid={`reservation-row-${i}`}>
                    <td className="px-5 py-3 text-cn-text-muted whitespace-nowrap">{(r.created_at || '').slice(0, 10)}</td>
                    <td className="px-5 py-3 font-medium text-cn-text dark:text-white">{r.email}</td>
                    <td className="px-5 py-3 text-cn-text-muted">{r.name || '—'}</td>
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-cn-primary/10 text-cn-primary border border-cn-primary/20">
                        {r.plan_id} <ArrowUpRight className="w-3 h-3" />
                      </span>
                    </td>
                    <td className="px-5 py-3 text-xs text-cn-text-muted font-mono">
                      {r.discipline || 'direct'}
                    </td>
                    <td className="px-5 py-3 text-right font-mono text-cn-text dark:text-white">
                      {r.current_monthly_earnings_usd != null
                        ? `$${Number(r.current_monthly_earnings_usd).toLocaleString()}`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminPricingPanel;
